import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { useGroupSecurityCohort } from '@/hooks/useGroupSecurityCohort';
import { usePermissions } from '@/hooks/usePermissions';

/**
 * Who sees each User Management surface — ONE rule for every place it can appear: the top-bar
 * teamspace menu's "Manage" section, the profile menu for single-teamspace users, and the Settings
 * side panel.
 *
 * DECISION (Isabel, 2026-10-12) — THE USERS SURFACE IS OWNER / SUPER ADMIN ONLY. `usersPage` reads
 * a role again, and the role it reads is `isRootSuperAdmin`, which is already `OWNER || SUPER_ADMIN`
 * (`usePermissions.ts:329`), so there is no separate Owner arm to add.
 *
 * This REVERSES the decision recorded here on 2026-10-08 (Kati), which made every role see all four
 * menus and had itself reversed the role half of #2351. That reasoning was accurate and is restated
 * because it is what this change knowingly gives up: a non-owner never reached an account-wide list
 * anyway — `MembersPage` enters directory mode only under `isAccountScope`, and
 * `accountUserDirectory` is OWNER/SUPER_ADMIN-only server-side regardless. What they reached was a
 * single teamspace's roster. So this does not close a leak; the backend had already closed it. It
 * is a product choice to stop showing the teamspace-scoped view to roles that could safely see it.
 *
 * KNOWN COST, accepted deliberately: `usersPage` is not only the Users tab. It also gates the
 * `/app/s/users` route, the Teamspaces route branch, and — via `WorkspacesPage`, which reads it as
 * `canViewMembers` — the member pop-up on a teamspace row. So an ADMIN or TEAM OWNER can no longer
 * open the roster of a teamspace they administer. That is a loss of function rather than a
 * tightened boundary, and it was raised before the change was made.
 *
 * The capability keys are untouched: `members.view` still means "members of the teamspaces you
 * belong to" in the registry and still gates those reads server-side. This flag decides what the UI
 * OFFERS, never what the backend permits.
 *
 * RELEASE COHORT (#2452) — every entry below is additionally gated on `useGroupSecurityCohort()`.
 * The Directory IS Group Security's user-facing surface, so an OFF-cohort account must not see any
 * of it; it gets the Account Members page back instead (SettingsLayout).
 *
 * Gating it HERE rather than at each render site is the point: these four entries are drawn in
 * three places — the Settings side panel, the top-bar teamspace "Manage" footer (`TeamScopeList` /
 * `TeamScopeButton`), and the profile menu (`UserMenu`) — and gating one twin and not the other is
 * exactly the defect `.claude/rules/frontend/white-label-branding.md` warns about. One predicate,
 * one place, every render path.
 */
export function useUserManagementAccess() {
  const { folderLevelSecurity } = useCompanyFeatures();
  const { isRootSuperAdmin } = usePermissions();
  const cohort = useGroupSecurityCohort();

  if (!cohort) {
    // OFF cohort: no Directory anywhere. Returned as a whole object rather than `&& cohort` on each
    // line so a future entry added to this hook is off by default for legacy accounts — the safe
    // direction, and one a per-line `&&` would silently miss.
    return { usersPage: false, users: false, teamspaces: false, roles: false, groups: false };
  }

  return {
    /**
     * Governs the `/app/s/users` route guard, the Settings "Users" row, the Teamspaces route's
     * branch, and the teamspace member pop-up (`WorkspacesPage` reads this as `canViewMembers`).
     *
     * NOT folded into `folderLevelSecurity`: the Users page is open on EVERY plan, so gating it on
     * the plan flag would hide it from owners of accounts with no Group Security at all. That
     * plan-independence predates this change and is preserved — the role is the only new condition.
     */
    usersPage: isRootSuperAdmin,
    /**
     * The Users shortcut in the top-bar Manage footer (`TeamScopeButton`) and the profile menu
     * (`UserMenu`). It points at the same `/app/s/users` route `usersPage` guards, so it carries
     * the same role rule — a shortcut to a page you cannot open is the #2413 shape in miniature.
     *
     * Still ALSO plan-gated: the shortcut is a Group Security affordance and does not exist on an
     * account without the feature, whereas the page itself is open on every plan.
     */
    users: folderLevelSecurity && isRootSuperAdmin,
    teamspaces: folderLevelSecurity,
    roles: folderLevelSecurity,
    groups: folderLevelSecurity,
  };
}
